import React, { useState, useEffect, useCallback } from 'react';
import TaskColumn from './TaskColumn';
import Button from './Button';
import LoadingSpinner from './LoadingSpinner';
import SearchInput from './SearchInput';
import './TaskBoard.css';

interface Task {
  id: string;
  title: string;
  description: string | null;
  status: string;
  owner: string | null;
  due_date: string | null;
  task_type: string | null;
  person_id: string | null;
  company_id: string | null;
  project_id: string | null;
  github_issue_url: string;
}

interface TaskBoardProps {
  onCreateTask: () => void;
  onEditTask: (taskId: string) => void;
}

const TaskBoard: React.FC<TaskBoardProps> = ({ onCreateTask, onEditTask }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [updating, setUpdating] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);

  // Fetch tasks
  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = new URLSearchParams();
      if (searchQuery) {
        params.append('q', searchQuery);
      }
      const res = await fetch(`/api/github-ops-tasks?${params.toString()}`);
      if (!res.ok) throw new Error('fetch failed');
      const json = (await res.json()) as { success: boolean; data: Task[] };
      if (!json.success) throw new Error('api failed');
      setTasks(json.data);
    } catch (err) {
      console.error('Failed to fetch tasks:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Handle status change (via dropdown or drag-and-drop)
  const handleStatusChange = async (taskId: string, newStatus: string) => {
    setUpdating(taskId);
    try {
      const res = await fetch(`/api/github-ops-tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('update failed');
      const json = (await res.json()) as { success: boolean };
      if (!json.success) throw new Error('api failed');

      // Optimistically update local state
      setTasks((prev) =>
        prev.map((task) =>
          task.id === taskId ? { ...task, status: newStatus } : task
        )
      );
    } catch (err) {
      console.error('Failed to update task status:', err);
      alert('Failed to update task status. Please try again.');
    } finally {
      setUpdating(null);
    }
  };

  // Handle bulk archive of all Done tasks
  const handleArchiveAllDone = async () => {
    // Calculate current Done tasks from fresh state
    const currentDoneTasks = tasks.filter((t) => t.status === 'Done');
    
    console.log('[Archive All] Starting with tasks:', currentDoneTasks.map(t => ({ id: t.id, title: t.title })));
    
    if (currentDoneTasks.length === 0) {
      console.log('[Archive All] No tasks to archive');
      return;
    }

    const confirmed = window.confirm(
      `Archive all ${currentDoneTasks.length} completed task${currentDoneTasks.length === 1 ? '' : 's'}?\n\nArchived tasks will no longer appear on the main task board.`
    );
    if (!confirmed) {
      console.log('[Archive All] User cancelled');
      return;
    }

    console.log('[Archive All] User confirmed, starting archive...');
    setArchiving(true);
    try {
      // Archive all Done tasks in parallel
      const results = await Promise.all(
        currentDoneTasks.map(async (task) => {
          console.log(`[Archive All] Archiving task ${task.id}...`);
          const res = await fetch(`/api/github-ops-tasks/${task.id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ status: 'Archived' }),
          });
          console.log(`[Archive All] Task ${task.id} response:`, res.status, res.ok);
          if (!res.ok) {
            const text = await res.text();
            console.error(`[Archive All] Task ${task.id} failed:`, text);
            throw new Error(`Failed to archive task ${task.id}: ${res.status}`);
          }
          const json = await res.json();
          console.log(`[Archive All] Task ${task.id} result:`, json);
          return json;
        })
      );

      console.log('[Archive All] All tasks archived, results:', results);
      console.log('[Archive All] Refreshing task list...');
      
      // Refresh the task list
      await fetchTasks();
      
      console.log('[Archive All] Task list refreshed');
    } catch (err) {
      console.error('[Archive All] Error:', err);
      alert('Failed to archive some tasks. Please try again.');
    } finally {
      setArchiving(false);
      console.log('[Archive All] Complete');
    }
  };

  // Group tasks by status
  const todoTasks = tasks.filter((t) => t.status === 'To do');
  const doingTasks = tasks.filter((t) => t.status === 'Doing');
  const doneTasks = tasks.filter((t) => t.status === 'Done');

  if (loading) {
    return (
      <div className="task-board-loading">
        <LoadingSpinner />
        <p>Loading tasks...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="task-board-error">
        <p>Failed to load tasks.</p>
        <Button variant="secondary" onClick={fetchTasks}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="task-board">
      <div className="task-board-header">
        <div className="task-board-search">
          <SearchInput
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search tasks..."
          />
        </div>
        <Button variant="primary" onClick={onCreateTask}>
          + New Task
        </Button>
      </div>

      <div className="task-board-columns">
        <TaskColumn
          title="To do"
          status="To do"
          tasks={todoTasks}
          onStatusChange={handleStatusChange}
          onEdit={onEditTask}
          onDrop={handleStatusChange}
        />
        <TaskColumn
          title="Doing"
          status="Doing"
          tasks={doingTasks}
          onStatusChange={handleStatusChange}
          onEdit={onEditTask}
          onDrop={handleStatusChange}
        />
        <TaskColumn
          title="Done"
          status="Done"
          tasks={doneTasks}
          onStatusChange={handleStatusChange}
          onEdit={onEditTask}
          onDrop={handleStatusChange}
          onArchiveAll={handleArchiveAllDone}
          archiving={archiving}
        />
      </div>

      {updating && (
        <div className="task-board-updating">
          <LoadingSpinner size="sm" />
          <span>Updating task...</span>
        </div>
      )}
    </div>
  );
};

export default TaskBoard;
